import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TablePagination,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useNavigate } from "react-router-dom";

import {
  DateTimePicker,
  LocalizationProvider,
  renderTimeViewClock,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getModule, getUserFootfallReport, getUserList } from "./services";

export default function FootFallReport() {
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedModule, setSelectedModule] = useState("all");
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0); // Add state for current page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Add state for rows per page
  const [totalRows, setTotalRows] = useState(0);

  const navigate = useNavigate();

  // Fetch users and modules on component mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [userRes, moduleRes] = await Promise.all([
          getUserList(),
          getModule(),
        ]);
        setUsers([
          { id: "all", email: "All Users" },
          ...userRes.data.payload,
        ]);
        setModules([
          { id: "all", name: "All Modules" },
          ...moduleRes.data.payload,
        ]);
      } catch (err) {
        console.error("Failed to fetch dropdown data:", err);
        setError("Failed to load users and modules.");
      }
    };
    fetchDropdownData();
  }, []);

  const handleApplyFilters = async () => {
    setLoading(true);
    setError(null);
    setShowResults(false);
    setTableData([]);

    const payload = {
      start_datetime: startDate.format("YYYY-MM-DD HH:mm:ss"),
      end_datetime: endDate.format("YYYY-MM-DD HH:mm:ss"),
      page: page + 1,
      page_size: rowsPerPage,
    };

    if (selectedUser !== "all") {
      payload.user_id = selectedUser;
    }
    if (selectedModule !== "all") {
      payload.module_id = selectedModule;
    }

    try {
      const response = await getUserFootfallReport(payload);
      setTableData(response.data.results.payload);
      setTotalRows(response.data.count);
      setShowResults(true);
    } catch (err) {
      console.error("Failed to fetch footfall report:", err);
      setError("Failed to fetch report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearDataFilters = async () => {
    setLoading(true);
    setError(null);
    setShowResults(false);
    setTableData([]);

    const payload = {
      start_datetime: startDate.format("YYYY-MM-DD HH:mm:ss"),
      end_datetime: endDate.format("YYYY-MM-DD HH:mm:ss"),
      page: page + 1,
      page_size: rowsPerPage,
    };

  
    try {
      const response = await getUserFootfallReport(payload);
      setTableData(response.data.results.payload);
      setTotalRows(response.data.count);
      setShowResults(true);
    } catch (err) {
      console.error("Failed to fetch footfall report:", err);
      setError("Failed to fetch report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    handleApplyFilters();
  }, [page, rowsPerPage]); // Run on initial component mount to display default data

  const handleClearFilters = () => {
    handleClearDataFilters()
    setSelectedUser("all");
    setSelectedModule("all");
    setStartDate(dayjs().subtract(7, "day"));
    setEndDate(dayjs());
    setShowResults(false);
    setTableData([]);
    setError(null);
  };

  return (
    <>
      <div className="containerWrap">
        <div className="packingHeader-wrap">
          <div className="packingTitle-wrap">
            <KeyboardBackspaceIcon
              onClick={() => navigate("/dashboard")}
              className="backArrow"
            />
            <Typography
              variant="h5"
              className="packingTitle"
              gutterBottom
              onClick={() => navigate("/dashboard")}
            >
              User Footfall Report
            </Typography>
          </div>
        </div>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Card sx={{ borderRadius: 2, mb: 4, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Filters
              </Typography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="user-select-label">User</InputLabel>
                    <Select
                      labelId="user-select-label"
                      label="User"
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="module-select-label">Module</InputLabel>
                    <Select
                      labelId="module-select-label"
                      label="Module"
                      value={selectedModule}
                      onChange={(e) => setSelectedModule(e.target.value)}
                    >
                      {modules.map((module) => (
                        <MenuItem key={module.id} value={module.id}>
                          {module.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      className="date-picker-production"
                      sx={{ width: "100%" }}
                      label="Start Date"
                      value={startDate}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      onChange={(newDate) => setStartDate(newDate)}
                      disableFuture
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          style={{ background: "#ffff" }}
                          InputProps={{
                            inputProps: { style: { padding: "16px" } },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      className="date-picker-production"
                      label="End Date"
                      sx={{ width: "100%" }}
                      value={endDate}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      onChange={(newDate) => setEndDate(newDate)}
                      disableFuture
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          style={{ background: "#ffff" }}
                          InputProps={{
                            inputProps: { style: { padding: "16px" } },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  sx={{ background: "#1c407a" }}
                  disabled={loading}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{ color: "#1c407a", border: "1px solid #1c407a" }}
                >
                  Clear
                </Button>
              </Box>
            </CardContent>
          </Card>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={{ mt: 4 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {showResults && !loading && !error && (
            <Box sx={{ mt: 4 }}>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#1c407a" }}>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        User Email
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Module
                      </TableCell>

                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Start Time
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        End Time
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Duration
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.length > 0 ? (
                      tableData.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.user_email}</TableCell>
                          <TableCell>{row.module_name}</TableCell>
                          <TableCell>
                            {row.start_time
                              ? dayjs(row.start_time).format(
                                  "YYYY-MM-DD HH:mm:ss"
                                )
                              : ""}
                          </TableCell>
                          <TableCell>
                            {row.end_time ? (
                              dayjs(row.end_time).format("YYYY-MM-DD HH:mm:ss")
                            ) : (
                              <span
                                style={{
                                  color: "green",
                                  fontWeight: "bold",
                                  animation: "blinker 1s linear infinite",
                                }}
                              >
                                Currently Using
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.end_time ? (
                              <span
                                style={{ color: "red", fontWeight: "bold" }}
                              >
                                {row.duration_minutes} min
                              </span>
                            ) : (
                              <span
                                style={{
                                  color: "green",
                                  fontWeight: "bold",
                                  animation: "blinker 1s linear infinite",
                                }}
                              >
                                {row.duration_minutes} min
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No data found for the selected filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalRows}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  sx={{
                    marginBottom: "0px",
                    "& .MuiTablePagination-selectLabel": {
                      marginBottom: 0, // override here
                    },
                    "& .MuiTablePagination-displayedRows": {
                      marginBottom: 0,
                    },
                  }}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0); // Reset to the first page when rows per page changes
                  }}
                />
              </TableContainer>
            </Box>
          )}
        </Container>
      </div>
    </>
  );
}